import { observable, action } from "mobx";
import Collection from "./CollectionHelper";
import Utils from "./Utils";
import DocumentStore from "../store/DocumentStore";

// "invalid" = failed the allowed-extension check at selection time.
// Kept separate from "failed" (a real upload attempt that failed).
export type UploadFileStatus =
  | "pending"
  | "uploading"
  | "success"
  | "failed"
  | "invalid";

export interface PickedFile {
  id: string;
  fileName: string;
  fileData: string;
  size: number;
}

export interface UploadFile extends PickedFile {
  status: UploadFileStatus;
  errorMessage: string;
}

// Matches the backend allow-list and the dropzone accept filter.
export const ALLOWED_EXTENSIONS = [
  "pdf",
  "jpeg",
  "jpg",
  "doc",
  "docx",
  "xls",
  "xlsx",
  "csv",
  "png",
  "txt",
  "msg",
];

// Shared metadata for one Add Document batch. Submission routing only
// applies when the batch has exactly one file.
export interface AgentBatchUploadMetadata {
  agentId: number;
  typeId: number;
  companyId: number;
  notes: string;
  isPrivate: boolean;
  isWorkflowChecked: boolean;
  isSendSubmission: boolean;
  isDontSend: boolean;
  submissionTypeIds: string;
}

// No cap on file count; block only when combined size exceeds 100MB.
const MAX_BATCH_SIZE_BYTES = 100 * 1024 * 1024;

// Singleton queue for the Add Documents batch upload. Lives outside the
// dialog/view model so a batch survives the dialog closing or the user
// navigating away and back.
class AgentDocUploadQueueContext {
  @observable batchFiles = new Collection<UploadFile>();
  @observable isBatchUploading: boolean = false;
  @observable batchMetadata: AgentBatchUploadMetadata | null = null;
  // True once the dialog was explicitly closed on a batch with
  // failed/uploading files still in it. Prevents auto-reopening the
  // dialog on a later page load; resets once the user re-engages.
  @observable wasDismissedByUser: boolean = false;

  // Computed from current file statuses so this message never goes stale.
  get batchFailureMessage(): string {
    if (this.batchFiles.values.some((f) => f.status === "invalid")) {
      return "One or more files could not be uploaded due to unsupported file formats.";
    }
    if (this.batchFiles.values.some((f) => f.status === "failed")) {
      return "One or more files could not be uploaded. Please try again.";
    }
    return "";
  }

  // Callback for whichever view model is currently interested in this
  // batch, notified once the whole batch succeeds.
  private onBatchFullySucceededCallback: (() => void) | null = null;

  setOnBatchFullySucceeded = (callback: (() => void) | null) => {
    this.onBatchFullySucceededCallback = callback;
  };

  // Fires after each individual file succeeds, independent of the overall
  // batch outcome, so the docs grid can stay current on a partial failure.
  private onFileUploadedCallback: (() => void) | null = null;

  setOnFileUploaded = (callback: (() => void) | null) => {
    this.onFileUploadedCallback = callback;
  };

  get combinedSize(): number {
    return this.batchFiles.values.reduce((sum, f) => sum + f.size, 0);
  }

  get isOverSizeLimit(): boolean {
    return this.combinedSize > MAX_BATCH_SIZE_BYTES;
  }

  // Flags unsupported file types at selection time instead of waiting for
  // a doomed upload attempt.
  @action addFiles = (newFiles: PickedFile[]) => {
    this.wasDismissedByUser = false;
    const added: UploadFile[] = newFiles.map((f) => {
      const ext = (f.fileName.split(".").pop() || "").toLowerCase();
      const isAllowed = ALLOWED_EXTENSIONS.includes(ext);
      return {
        ...f,
        status: isAllowed ? "pending" : "invalid",
        errorMessage: isAllowed
          ? ""
          : "One or more files could not be uploaded due to unsupported file formats.",
      };
    });
    this.batchFiles.values = [...this.batchFiles.values, ...added];
  };

  @action removeFile = (id: string) => {
    this.batchFiles.values = this.batchFiles.values.filter((f) => f.id !== id);
  };

  @action clearInvalidFiles = () => {
    this.batchFiles.values = this.batchFiles.values.filter(
      (f) => f.status !== "invalid"
    );
  };

  // Closing the dialog clears every file, pending or failed. Reopening
  // always starts blank.
  @action clearUnsubmittedFiles = () => {
    this.batchFiles.values = [];
    this.wasDismissedByUser = true;
  };

  @action clearBatch = () => {
    this.batchFiles.values = [];
    this.isBatchUploading = false;
    this.batchMetadata = null;
  };

  // Not awaited by callers - the upload continues independently of
  // whether the dialog or view model that started it still exists.
  @action startBatchUpload = (metadata: AgentBatchUploadMetadata) => {
    if (this.isBatchUploading || this.batchFiles.values.length === 0) {
      return;
    }
    this.batchMetadata = metadata;
    this.isBatchUploading = true;
    this.wasDismissedByUser = false;
    this.runBatchUpload();
  };

  @action private runBatchUpload = async () => {
    const metadata = this.batchMetadata;
    if (!metadata) {
      this.isBatchUploading = false;
      return;
    }
    const filesToUpload = this.batchFiles.values.filter(
      (f) => f.status !== "invalid"
    );

    // One file at a time so a queued file stays removable until its own
    // turn starts. Each file succeeds or fails independently.
    await Utils.uploadWithConcurrencyLimit(
      filesToUpload,
      async (file) => {
        // The file may have been removed while still queued - skip it,
        // no API call.
        const stillSelected = this.batchFiles.values.some(
          (f) => f.id === file.id
        );
        if (!stillSelected) {
          return;
        }
        file.status = "uploading";
        file.errorMessage = "";
        try {
          await DocumentStore.CreateUpdateDocument({
            id: 0,
            fName: "",
            lName: "",
            agentId: metadata.agentId,
            typeId: metadata.typeId,
            companyId: metadata.companyId,
            notes: metadata.notes,
            isPrivate: metadata.isPrivate,
            createdDate: new Date(),
            fileData: file.fileData,
            licenseStatusId: 0,
            // Only PDFs are routed to the Scrub Queue.
            isWorkflowChecked:
              metadata.isWorkflowChecked &&
              (file.fileName.split(".").pop() || "").toLowerCase() === "pdf",
            isSendSubmission: metadata.isSendSubmission,
            isDontSend: metadata.isDontSend,
            ignoreForAgeCalculation: false,
            submissionTypeIds: metadata.submissionTypeIds,
            fileName: file.fileName,
          });
          // Remove immediately on success rather than waiting for the
          // whole batch, and refresh the grid right away.
          file.status = "success";
          this.batchFiles.values = this.batchFiles.values.filter(
            (f) => f.id !== file.id
          );
          if (this.onFileUploadedCallback) {
            this.onFileUploadedCallback();
          }
        } catch (e) {
          file.status = "failed";
          file.errorMessage =
            "One or more files could not be uploaded. Please try again.";
        }
      },
      1
    );

    this.isBatchUploading = false;
    const anyFailed = this.batchFiles.values.some((f) => f.status === "failed");
    if (anyFailed) {
      // Keep failed files visible for retry; drop the ones that succeeded.
      this.batchFiles.values = this.batchFiles.values.filter(
        (f) => f.status !== "success"
      );
    } else {
      const callback = this.onBatchFullySucceededCallback;
      this.onBatchFullySucceededCallback = null;
      this.clearBatch();
      if (callback) {
        callback();
      }
    }
  };
}

export default new AgentDocUploadQueueContext();
